// src/pages/PrivacyPolicy.jsx
import React from 'react';
import { Box, Typography, Link, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

export default function PrivacyPolicy() {
    return (
        <>
            <Box
                sx={{
                    maxWidth: 800,
                    mx: 'auto',
                    py: { xs: 4, md: 6 },
                    px: { xs: 2, md: 2 },
                }}
            >
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.6rem', sm: '1.8rem', md: undefined },
                        lineHeight: { xs: 1.25, sm: 1.25, md: undefined },
                    }}
                >
                    Privacy Policy
                </Typography>

                <Typography variant="caption" display="block" gutterBottom>
                    Last updated: September 10, 2025
                </Typography>

                <Typography
                    paragraph
                    sx={{ fontSize: { xs: '1rem', md: undefined }, lineHeight: { xs: 1.7, md: 1.75 } }}
                >
                    This Privacy Policy describes how the Piedmont Historical Society (“we,” “us,” or “our”)
                    collects, uses, and shares personal information when you visit our website and use our
                    services. By using our site, you agree to this Policy.
                </Typography>

                {/* Summary */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Quick Summary
                </Typography>
                <Box component="ul" sx={{ pl: { xs: 3, md: 3 }, lineHeight: { xs: 1.7, md: 1.8 }, mb: 2 }}>
                    <li>We collect only what’s needed to run contact, booking, volunteer, newsletter, donation, and membership features.</li>
                    <li>Payments are handled by PayPal; we do not receive or store card numbers on our servers.</li>
                    <li>You can unsubscribe from our newsletter at any time, and request updates or deletion of your data.</li>
                    <li>We do not sell personal information.</li>
                </Box>

                {/* Information We Collect */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Information We Collect
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    We collect personal information that you provide directly to us through forms and site features:
                </Typography>
                <Box component="ul" sx={{ pl: { xs: 3, md: 3 }, lineHeight: { xs: 1.7, md: 1.8 }, mb: 2 }}>
                    <li>
                        <strong>Contact Messages:</strong> First and last name, email address, phone (optional), and your
                        message when you submit our contact form.
                    </li>
                    <li>
                        <strong>Bookings & Visits:</strong> First and last name, email, phone, visit type (individual, group,
                        or school), group size or number of students, school details (if applicable), requested date/time, and
                        optional comments to help schedule your visit.
                    </li>
                    <li>
                        <strong>Volunteers:</strong> Contact details (name, email, phone, address), availability and interests/
                        skills you select, plus any additional notes you choose to share.
                    </li>
                    <li>
                        <strong>Newsletter:</strong> Email address to send you updates; we use a confirmation (“double opt‑in”)
                        link to verify your address.
                    </li>
                    <li>
                        <strong>Membership:</strong> Contact details plus the membership type you select (Single, Couple, Family),
                        and payment method (PayPal or check).
                    </li>
                    <li>
                        <strong>Donations:</strong> The amount and optional note you enter before completing payment with PayPal.
                    </li>
                    <li>
                        <strong>Technical data:</strong> Basic server logs (e.g., IP address and request metadata) needed to secure
                        and operate the site. We do not run behavioral ad tracking or analytics on this site.
                    </li>
                </Box>

                {/* How We Use Info */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    How We Use Your Information
                </Typography>
                <Box component="ul" sx={{ pl: { xs: 3, md: 3 }, lineHeight: { xs: 1.7, md: 1.8 }, mb: 2 }}>
                    <li>Respond to your inquiries and coordinate visits, bookings, volunteer opportunities, and memberships.</li>
                    <li>Process memberships and donations (via PayPal) and send confirmations or follow‑ups as needed.</li>
                    <li>Send newsletters after you confirm your subscription; you can unsubscribe at any time.</li>
                    <li>Improve the reliability and security of our website and services.</li>
                </Box>

                {/* Payments & Donations */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Payments & Donations
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    We use PayPal to process membership dues and donations. When you pay, you are redirected to or interact
                    with PayPal’s secure widgets. We do not receive or store your full payment card details on our servers.
                    For memberships paid with PayPal, your membership is marked as confirmed after payment. If you choose to
                    mail a check, we record your application but leave it unconfirmed until processed. We store the
                    membership type and payment method you selected in our records.
                </Typography>

                {/* Newsletter */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Newsletter Subscriptions
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    If you sign up for the newsletter, we send a confirmation email with a one‑time link. Tokens expire
                    after a limited period for security. You can unsubscribe at any time using the link in our emails or
                    from the confirmation page.
                </Typography>

                {/* Bookings & Volunteers */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Bookings, Volunteers, and Membership Records
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    We use the details you provide to schedule your visit, coordinate volunteer opportunities, and maintain
                    membership records. Volunteer and membership information helps us contact you about opportunities and
                    benefits, and to keep accurate organizational records.
                </Typography>

                {/* Maps & Third Parties */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Maps, Social Links, and Third‑Party Services
                </Typography>
                <Box component="ul" sx={{ pl: { xs: 3, md: 3 }, lineHeight: { xs: 1.7, md: 1.8 }, mb: 2 }}>
                    <li>
                        <strong>Maps:</strong> Our Location page uses OpenStreetMap tiles via a mapping library to display the
                        museum location; loading the map may allow those services to receive your IP address and basic
                        browser information.
                    </li>
                    <li>
                        <strong>PayPal:</strong> PayPal may set cookies or similar technologies to operate checkout and prevent
                        fraud. See PayPal’s privacy policy for details.
                    </li>
                    <li>
                        <strong>Social media:</strong> We link to our official Facebook and Instagram pages. If you click those
                        links, the respective platforms’ privacy policies apply.
                    </li>
                </Box>

                {/* Cookies */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Cookies & Similar Technologies
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    We use only the cookies necessary to operate core features (e.g., PayPal checkout, basic session
                    behavior). We do not run analytics or advertising cookies on this site.
                </Typography>

                {/* Information Sharing */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Information Sharing & Disclosure
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    We do not sell or rent your personal information. We may share data with service providers that help us
                    operate the website (e.g., email delivery and payment processing) and with authorities when required by
                    law or to protect our rights or users.
                </Typography>

                {/* Security */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Data Security
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    We employ reasonable security measures to protect your information, including HTTPS encryption in transit
                    and server‑side safeguards designed to reduce abuse (for example, security headers and rate limiting).
                    However, no method of transmission or storage is 100% secure, so please use care when submitting
                    information online.
                </Typography>

                {/* Retention */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Data Retention
                </Typography>
                <Box component="ul" sx={{ pl: { xs: 3, md: 3 }, lineHeight: { xs: 1.7, md: 1.8 }, mb: 2 }}>
                    <li>Contact messages and booking records are kept as needed to respond and maintain scheduling records.</li>
                    <li>Volunteer and membership records are retained while active and for a reasonable period afterward for organizational record‑keeping.</li>
                    <li>Newsletter email addresses are stored until you unsubscribe; unsubscribing removes your address from the mailing list.</li>
                </Box>

                {/* Your Rights */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Your Choices & Rights
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    You may request access to, update, or ask us to delete your personal information, subject to applicable
                    law. You can also unsubscribe from the newsletter at any time. To make a request, please{' '}
                    <Link component={RouterLink} to="/contact" underline="hover">
                        contact us
                    </Link>.
                </Typography>

                {/* Children */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Children’s Privacy
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    Our website is not directed to children under 13, and we do not knowingly collect personal information
                    from children under 13 without appropriate consent. If you believe a child has provided personal
                    information to us, please contact us so we can remove it.
                </Typography>

                {/* Changes */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Changes to This Policy
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    We may update this Privacy Policy from time to time. The date at the top indicates when it was last
                    revised.
                </Typography>

                {/* Contact */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: undefined } }}
                >
                    Contact Us
                </Typography>
                <Typography paragraph sx={{ fontSize: { xs: '1rem', md: undefined } }}>
                    If you have questions about this Privacy Policy or our practices, please{' '}
                    <Link component={RouterLink} to="/contact" underline="hover">
                        contact us
                    </Link>.
                </Typography>
            </Box>

            {/* Mobile-only "Back to the top" */}
            <Box sx={{ textAlign: 'center', mt: 3, display: { xs: 'block', lg: 'none' } }}>
                <Button
                    variant="outlined"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    sx={{ borderRadius: 9999, px: 4, width: { xs: '100%', sm: 'auto' } }}
                    aria-label="Back to the top"
                >
                    Back to the top
                </Button>
            </Box>
        </>
    );
}
